import { formatEuros, parseEuros, VERHUURTYPES } from "@bivak/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { nl } from "../../copy/nl.js";
import {
  ApiError,
  type BeheerTarief,
  type EnergieTarief,
  fetchAdminTarieven,
  publiceerEnergieprijs,
  publiceerVerhuurtarief,
} from "../../lib/api.js";
import { asCents, formatDayLong } from "../../lib/format.js";

const INPUT =
  "min-h-11 w-full rounded-lg border-2 border-lijn bg-kaart px-3 text-sm outline-none focus:border-dennen";
const KNOP_PRIMAIR =
  "inline-flex min-h-11 items-center rounded-xl bg-rood px-5 font-semibold text-kaart text-sm hover:bg-rood-donker disabled:opacity-50";
const KNOP_STIL = "min-h-11 px-2 font-semibold text-mist text-sm hover:text-inkt";

/**
 * SCHERM: tariff management (0.25). Everything here PUBLISHES a new version
 * effective from a chosen date — nothing edits an old price, so a settlement
 * from last year keeps recomputing identically (rules/domain.md).
 */
export function BeheerTarievenPage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["admin-tarieven"],
    queryFn: fetchAdminTarieven,
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });

  if (isPending) return <p className="py-16 text-center text-mist">{nl.algemeen.laden}</p>;
  if (isError || !data) return <p className="py-16 text-center text-mist">{nl.algemeen.fout}</p>;

  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 font-bold font-display text-2xl tracking-tight">
        {nl.beheer.tarieven.titel}
      </h1>
      <p className="mb-6 max-w-xl text-[13px] text-mist">{nl.beheer.tarieven.uitleg}</p>

      <Kop>{nl.beheer.tarieven.verhuurKop}</Kop>
      <div className="mb-6 grid grid-cols-1 gap-2">
        {VERHUURTYPES.map((type) => (
          <VerhuurKaart
            key={type}
            verhuurtype={type}
            tarief={data.verhuur.find((kandidaat) => kandidaat.verhuurtype === type)}
            komend={data.komend.find((kandidaat) => kandidaat.verhuurtype === type)}
          />
        ))}
      </div>

      <Kop>{nl.beheer.tarieven.energieKop}</Kop>
      <div className="grid grid-cols-1 gap-2">
        {data.energie.map((meter) => (
          <EnergieKaart key={meter.meter} meter={meter} />
        ))}
      </div>
    </div>
  );
}

function Kop({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 font-mono font-semibold text-[11px] text-rood uppercase tracking-widest">
      {children}
    </h2>
  );
}

function huidigeLijn(tarief: BeheerTarief): string {
  const delen: string[] = [];
  if (tarief.perDayCents !== null) {
    delen.push(nl.kalender.prijsPerDag(formatEuros(asCents(tarief.perDayCents))));
  }
  if (tarief.perPersonPerNightCents !== null) {
    delen.push(
      nl.kalender.prijsPerPersoonNacht(formatEuros(asCents(tarief.perPersonPerNightCents))),
    );
  }
  if (tarief.minimumCents > 0) {
    delen.push(nl.kalender.prijsMinimum(formatEuros(asCents(tarief.minimumCents))));
  }
  delen.push(nl.beheer.tarieven.waarborgLijn(formatEuros(asCents(tarief.waarborgCents))));
  return delen.join(" · ");
}

function VerhuurKaart({
  verhuurtype,
  tarief,
  komend,
}: {
  verhuurtype: "dag" | "weekend" | "kamp";
  tarief: BeheerTarief | undefined;
  komend: (BeheerTarief & { vanaf?: string }) | undefined;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [prijs, setPrijs] = useState("");
  const [minimum, setMinimum] = useState("");
  const [waarborg, setWaarborg] = useState("");
  const [vanaf, setVanaf] = useState("");

  const isDag = verhuurtype === "dag";
  const prijsCents = parseEuros(prijs);
  const minimumCents = isDag ? 0 : parseEuros(minimum);
  const waarborgCents = parseEuros(waarborg);
  const geldig =
    prijsCents !== null &&
    minimumCents !== null &&
    waarborgCents !== null &&
    /^\d{4}-\d{2}-\d{2}$/.test(vanaf);

  const publiceer = useMutation({
    mutationFn: publiceerVerhuurtarief,
    onSuccess: () => {
      setOpen(false);
      setPrijs("");
      setMinimum("");
      setWaarborg("");
      // The public cards, the quote and this screen all read these rows.
      queryClient.invalidateQueries({ queryKey: ["admin-tarieven"] });
      queryClient.invalidateQueries({ queryKey: ["tarieven"] });
      queryClient.invalidateQueries({ queryKey: ["quote"] });
    },
  });

  return (
    <section className="rounded-2xl border border-lijn bg-kaart p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold font-display text-base">
            {nl.kalender.types[verhuurtype].naam}
          </h3>
          <p className="mt-0.5 font-mono text-[13px]">
            {tarief ? huidigeLijn(tarief) : nl.beheer.tarieven.ontbreekt}
          </p>
          {komend?.vanaf && (
            <p className="mt-0.5 text-[11.5px] text-mist">
              {nl.beheer.tarieven.gepland(formatDayLong(komend.vanaf))}: {huidigeLijn(komend)}
            </p>
          )}
        </div>
        {!open && (
          <button type="button" onClick={() => setOpen(true)} className={`${KNOP_STIL} shrink-0`}>
            {nl.beheer.tarieven.nieuwTarief}
          </button>
        )}
      </div>

      {open && (
        <form
          className="mt-3 grid grid-cols-1 gap-3 border-lijn border-t pt-3 sm:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (
              !geldig ||
              publiceer.isPending ||
              prijsCents === null ||
              minimumCents === null ||
              waarborgCents === null
            )
              return;
            publiceer.mutate({
              verhuurtype,
              perDayCents: isDag ? prijsCents : null,
              perPersonPerNightCents: isDag ? null : prijsCents,
              minimumCents,
              waarborgCents,
              vanaf,
            });
          }}
        >
          <BedragVeld
            label={
              isDag ? nl.beheer.tarieven.velden.perDag : nl.beheer.tarieven.velden.perPersoonNacht
            }
            waarde={prijs}
            fout={prijs !== "" && prijsCents === null}
            onChange={setPrijs}
          />
          {!isDag && (
            <BedragVeld
              label={nl.beheer.tarieven.velden.minimum}
              waarde={minimum}
              fout={minimum !== "" && minimumCents === null}
              onChange={setMinimum}
            />
          )}
          <BedragVeld
            label={nl.beheer.tarieven.velden.waarborg}
            waarde={waarborg}
            fout={waarborg !== "" && waarborgCents === null}
            onChange={setWaarborg}
          />
          <label className="block">
            <span className="mb-1.5 block font-semibold text-xs">
              {nl.beheer.tarieven.velden.vanaf}
            </span>
            <input
              type="date"
              value={vanaf}
              onChange={(event) => setVanaf(event.target.value)}
              required
              className={INPUT}
            />
          </label>
          <div className="flex items-center gap-3 sm:col-span-3">
            <button
              type="submit"
              disabled={!geldig || publiceer.isPending}
              className={KNOP_PRIMAIR}
            >
              {nl.beheer.tarieven.publiceer}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className={`${KNOP_STIL} shrink-0`}
            >
              {nl.beheer.tarieven.annuleer}
            </button>
            {publiceer.isError && (
              <p className="text-rood-donker text-sm">
                {publiceer.error instanceof ApiError ? publiceer.error.message : nl.algemeen.fout}
              </p>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

function EnergieKaart({ meter }: { meter: EnergieTarief }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [prijs, setPrijs] = useState("");
  const [vanaf, setVanaf] = useState("");

  const prijsCents = parseEuros(prijs);
  const geldig = prijsCents !== null && /^\d{4}-\d{2}-\d{2}$/.test(vanaf);

  const publiceer = useMutation({
    mutationFn: publiceerEnergieprijs,
    onSuccess: () => {
      setOpen(false);
      setPrijs("");
      queryClient.invalidateQueries({ queryKey: ["admin-tarieven"] });
    },
  });

  return (
    <section className="rounded-2xl border border-lijn bg-kaart p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold font-display text-base">
            {nl.beheer.tarieven.meters[meter.meter] ?? meter.meter}
          </h3>
          <p className="mt-0.5 font-mono text-[13px]">
            {nl.beheer.tarieven.perEenheid(formatEuros(asCents(meter.priceCents)), meter.unit)}{" "}
            <span className="text-mist">
              · {nl.beheer.tarieven.sinds(formatDayLong(meter.validFrom))}
            </span>
          </p>
          {meter.gepland.length > 0 && (
            <p className="mt-1 text-[11.5px] text-mist">
              {meter.gepland
                .map(
                  (versie) =>
                    `${nl.beheer.tarieven.gepland(formatDayLong(versie.validFrom))}: ${formatEuros(asCents(versie.priceCents))}`,
                )
                .join(" · ")}
            </p>
          )}
          {meter.historiek.length > 0 && (
            <p className="mt-1 text-[11.5px] text-mist">
              {nl.beheer.tarieven.historiekKop}:{" "}
              {meter.historiek
                .map(
                  (versie) =>
                    `${formatEuros(asCents(versie.priceCents))} (${nl.beheer.tarieven.sinds(formatDayLong(versie.validFrom))})`,
                )
                .join(" · ")}
            </p>
          )}
        </div>
        {!open && (
          <button type="button" onClick={() => setOpen(true)} className={`${KNOP_STIL} shrink-0`}>
            {nl.beheer.tarieven.nieuwTarief}
          </button>
        )}
      </div>

      {open && (
        <form
          className="mt-3 grid grid-cols-1 gap-3 border-lijn border-t pt-3 sm:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!geldig || publiceer.isPending || prijsCents === null) return;
            publiceer.mutate({ meter: meter.meter, priceCents: prijsCents, vanaf });
          }}
        >
          <BedragVeld
            label={nl.beheer.tarieven.velden.prijs}
            waarde={prijs}
            fout={prijs !== "" && prijsCents === null}
            onChange={setPrijs}
          />
          <label className="block">
            <span className="mb-1.5 block font-semibold text-xs">
              {nl.beheer.tarieven.velden.vanaf}
            </span>
            <input
              type="date"
              value={vanaf}
              onChange={(event) => setVanaf(event.target.value)}
              required
              className={INPUT}
            />
          </label>
          <div className="flex items-center gap-3 sm:col-span-3">
            <button
              type="submit"
              disabled={!geldig || publiceer.isPending}
              className={KNOP_PRIMAIR}
            >
              {nl.beheer.tarieven.publiceer}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className={`${KNOP_STIL} shrink-0`}
            >
              {nl.beheer.tarieven.annuleer}
            </button>
            {publiceer.isError && (
              <p className="text-rood-donker text-sm">
                {publiceer.error instanceof ApiError ? publiceer.error.message : nl.algemeen.fout}
              </p>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

function BedragVeld({
  label,
  waarde,
  fout,
  onChange,
}: {
  label: string;
  waarde: string;
  fout: boolean;
  onChange: (waarde: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-semibold text-xs">{label}</span>
      <input
        // Text, not number: volunteers type "4,50" — parseEuros (domain,
        // tested) is the only path from this string to money.
        inputMode="decimal"
        value={waarde}
        onChange={(event) => onChange(event.target.value)}
        required
        className={fout ? `${INPUT} border-rood` : INPUT}
      />
      {fout && (
        <span className="mt-1 block text-[11.5px] text-rood-donker">
          {nl.beheer.tarieven.ongeldigBedrag}
        </span>
      )}
    </label>
  );
}
